import { useCallback, useEffect, useMemo, useState } from 'react';

import { generateTaxBreakdownForInvoice } from '@commenda/api';
import { useIndirectTaxCustomer } from '@commenda/api/indirect-tax/customers';
import {
  IndirectTaxJurisdictionType,
  useAllIndirectTaxRegistrations,
} from '@commenda/api/indirect-tax/registrations';
import {
  useIndirectTaxTransactionExtended,
  useIndirectTaxTransactionLineItems,
} from '@commenda/api/indirect-tax/transactions';

import {
  IndirectTaxTransactionHelper,
  IndirectTaxTransactionMapper,
} from '../helpers';
import {
  TIndirectTaxTransactionDetail,
  TIndirectTaxTransactionRegion,
} from '../types';

interface UseIndirectTaxTransactionDetailProps {
  transactionId: string;
  corporationId: string;
}

interface UseIndirectTaxTransactionDetailReturn {
  transaction: TIndirectTaxTransactionDetail | null;
  region: TIndirectTaxTransactionRegion;
  isLoading: boolean;
  error: unknown;
  mutate: () => void;
  onGenerateTaxBreakdown: () => Promise<void>;
  isGeneratingTaxBreakdown: boolean;
}

const TAX_BREAKDOWN_POLLING_INTERVAL_MS = 3000;
const TAX_BREAKDOWN_POLLING_TIMEOUT_MS = 60000;

export function useIndirectTaxTransactionDetail({
  transactionId,
  corporationId,
}: UseIndirectTaxTransactionDetailProps): UseIndirectTaxTransactionDetailReturn {
  const [isGeneratingTaxBreakdown, setIsGeneratingTaxBreakdown] =
    useState(false);

  const {
    data: rawTransaction,
    isLoading: isLoadingTransaction,
    error: transactionError,
    mutate,
  } = useIndirectTaxTransactionExtended({
    transactionId,
    corporationId,
    extendedFields: ['shipping_address', 'ship_from_address', 'customer'],
  });

  const {
    data: rawLineItems,
    isLoading: isLoadingLineItems,
    error: lineItemsError,
    mutate: refetchLineItems,
  } = useIndirectTaxTransactionLineItems({
    transactionId,
    corporationId,
    extendedFields: ['product', 'tax_breakdown'],
    refreshInterval: isGeneratingTaxBreakdown
      ? TAX_BREAKDOWN_POLLING_INTERVAL_MS
      : 0,
  });

  const customerId =
    rawTransaction?.customer_id ||
    rawTransaction?.customer_details?.customer_id ||
    undefined;

  const { customer: rawCustomer, isLoading: isLoadingCustomer } =
    useIndirectTaxCustomer({
      customerId,
      corporationId: corporationId ? Number(corporationId) : undefined,
    });

  const destinationState =
    rawTransaction?.customer_details?.shipping_address?.state ??
    rawTransaction?.shipping_address?.state ??
    undefined;

  const { data: registrations } = useAllIndirectTaxRegistrations({
    filters: {
      corporationId: corporationId ? Number(corporationId) : undefined,
      states: destinationState ? [destinationState] : undefined,
      jurisdictionTypes: [
        IndirectTaxJurisdictionType.STATE_OR_PROVINCE,
        IndirectTaxJurisdictionType.STATE,
      ],
    },
  });

  const registrationId = destinationState ? registrations?.[0]?.id : undefined;

  const isLoading =
    isLoadingTransaction || isLoadingLineItems || isLoadingCustomer;
  const error = transactionError || lineItemsError;

  const onGenerateTaxBreakdown = useCallback(async () => {
    setIsGeneratingTaxBreakdown(true);

    try {
      const response = await generateTaxBreakdownForInvoice(
        Number(corporationId),
        transactionId
      );

      if (!response.success) {
        throw new Error(
          response.error || 'Failed to start tax breakdown generation'
        );
      }

      await refetchLineItems?.();
    } catch (error) {
      setIsGeneratingTaxBreakdown(false);
      throw error;
    }
  }, [corporationId, transactionId, refetchLineItems]);

  useEffect(() => {
    if (!isGeneratingTaxBreakdown || !rawLineItems) {
      return;
    }

    const hasBreakdown = rawLineItems.some(
      (item) => item.tax_breakdown && item.tax_breakdown.length > 0
    );

    if (hasBreakdown) {
      setIsGeneratingTaxBreakdown(false);
    }
  }, [isGeneratingTaxBreakdown, rawLineItems]);

  useEffect(() => {
    if (!isGeneratingTaxBreakdown) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsGeneratingTaxBreakdown(false);
    }, TAX_BREAKDOWN_POLLING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isGeneratingTaxBreakdown]);

  const { transaction, region } = useMemo(() => {
    if (!rawTransaction) {
      return {
        transaction: null,
        region: TIndirectTaxTransactionRegion.ALL_REGIONS,
      };
    }

    const mapped = IndirectTaxTransactionMapper.mapExtendedToDetail(
      rawTransaction,
      rawLineItems ?? []
    );

    if (rawCustomer) {
      mapped.customer = {
        id: rawCustomer.id ?? '',
        name: rawCustomer.name ?? '',
        email: rawCustomer.email ?? '',
      };
    }

    mapped.lineItems = mapped.lineItems.map((lineItem) =>
      lineItem.taxLogic
        ? {
            ...lineItem,
            taxLogic: { ...lineItem.taxLogic, registrationId, customerId },
          }
        : lineItem
    );

    const detectedRegion =
      IndirectTaxTransactionHelper.getRegionFromCountryOrTradeBloc(
        mapped.countryOrTradeBloc
      );

    return {
      transaction: mapped,
      region: detectedRegion,
    };
  }, [rawTransaction, rawLineItems, rawCustomer, registrationId, customerId]);

  return {
    transaction,
    region,
    isLoading,
    error,
    mutate: () => mutate?.(),
    onGenerateTaxBreakdown,
    isGeneratingTaxBreakdown,
  };
}
